import { LinkifiedSegments } from "@/src/components/ui-kit/linkified-text";
import ReactionGlyph from "@/src/components/posts/reactions/reaction-glyph";
import { useReactToComment } from "@/src/hooks/features/posts/use-react-to-comment";
import { PostComment, REACTIONS, ReactionType } from "@/src/models/post.model";
import { animateBounce } from "@/src/utils/animation.utils";
import { flattenForClamp } from "@/src/utils/string.utils";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Animated,
  Platform,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
  Pressable,
} from "react-native";
import { SelectableItem } from "@/src/components/action-modals/item-selection/item-row";
import { useTheme } from "@/src/styles/theme-provider";
import BlocksAvatar from "../../ui-kit/blocks-avatar";
import MentionedUserPill from "../mentioned-user-pill";
import ReactionPicker from "../reactions/reaction-picker";
import { LikeButton } from "./bottom-buttons/bottom-buttons";

dayjs.extend(relativeTime);

type CommentItemProps = Readonly<{
  comment: PostComment;
  level?: number;
  parentAuthor?: string;
  onReply?: (commentId: string, body: string) => void;
}>;

export const CommentItem = ({
  comment,
  level = 0,
  parentAuthor,
  onReply,
}: CommentItemProps) => {
  const { themeVars } = useTheme();
  const { t } = useTranslation();
  const { width: windowWidth } = useWindowDimensions();
  const indent = Math.min(level, 2) * (windowWidth < 768 ? 24 : 50);

  const MAX_LINES = 3;

  // Same reason as post-card: folding the author's line breaks while collapsed
  // keeps the clamp's "…" at the end of the last visible line.
  const previewContent = flattenForClamp(comment.content);

  const [expanded, setExpanded] = useState(false);
  const [showSeeMore, setShowSeeMore] = useState(false);
  const [fullHeight, setFullHeight] = useState(0);
  const [limitedHeight, setLimitedHeight] = useState(0);

  // Reset when comment changes
  useEffect(() => {
    setExpanded(false);
    setShowSeeMore(false);
    setFullHeight(0);
    setLimitedHeight(0);
  }, [comment.ItemId]);

  // Determine if "See More" should be shown
  useEffect(() => {
    if (fullHeight > 0 && limitedHeight > 0) {
      // Add a small threshold to avoid false positives due to minor layout differences
      const needsExpansion = fullHeight > limitedHeight + 5;
      setShowSeeMore(needsExpansion);
    }
  }, [fullHeight, limitedHeight]);

  const [showPicker, setShowPicker] = useState(false);
  const likeAnim = useRef(new Animated.Value(1)).current;
  const { userReaction, handleReact } = useReactToComment(comment);
  const hoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleHoverIn = () => {
    hoverTimeout.current = setTimeout(() => setShowPicker(true), 250);
  };

  const handleHoverOut = () => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => setShowPicker(false), 300);
  };

  const handleReactionSelect = (reaction: ReactionType, fromPicker = false) => {
    animateBounce(likeAnim);
    if (!fromPicker && userReaction?.type) {
      handleReact(null);
    } else {
      handleReact(reaction);
    }
    setShowPicker(false);
  };

  const topReactions = Object.entries(comment.reactionCount || {})
    .filter(([, count]) => (count ?? 0) > 0)
    .sort(([, a], [, b]) => (b ?? 0) - (a ?? 0))
    .slice(0, 3)
    .map(([id]) => REACTIONS.find((rxn) => rxn.id === id))
    .filter(Boolean) as typeof REACTIONS;

  const reactionTotal = Object.values(comment.reactionCount || {}).reduce(
    (sum, val) => (sum ?? 0) + (val ?? 0),
    0,
  );

  return (
    <View style={{ marginTop: 8 }}>
      <View className="flex-row gap-3" style={{ marginLeft: indent }}>
        <BlocksAvatar
          userId={comment.authorId}
          fallbackImageUrl={comment.authorSnapshot?.profileImageUrl}
          firstName={comment.authorSnapshot?.firstName}
          lastName={comment.authorSnapshot?.lastName}
          size={36}
        />

        <View
          className="rounded-[12px] px-4 py-2"
          style={{
            backgroundColor: themeVars["--color-ash-default"] as string,
            flexShrink: 1,
            maxWidth: "85%",
          }}
        >
          <View className="flex-row justify-between items-center gap-2">
            <Text
              className="font-medium text-sm text-onyx flex-1"
              numberOfLines={1}
            >
              {comment.authorSnapshot.firstName} {comment.authorSnapshot.lastName}
            </Text>
            <Text className="text-xs" style={{ color: themeVars["--color-slate-default"] as string }}>
              {dayjs(comment.CreatedDate).fromNow()}
            </Text>
          </View>

          {/* Hidden full height measurement - EXACT same styling */}
          {!expanded && (
            <Text
              className="text-sm text-onyx pt-1 absolute opacity-0 z-[-1]"
              onLayout={(e) => setFullHeight(e.nativeEvent.layout.height)}
            >
              {parentAuthor && `@${parentAuthor} `}
              {previewContent}
            </Text>
          )}

          {/* Visible text */}
          <Text
            className="text-sm text-onyx pt-1"   // ← Changed: consistent pt-1
            numberOfLines={expanded ? undefined : MAX_LINES}
            ellipsizeMode="tail"
            onLayout={(e) => {
              if (!expanded) {
                setLimitedHeight(e.nativeEvent.layout.height);
              }
            }}
          >
            {parentAuthor && (
              <Text style={{ color: themeVars["--color-primary-default"] as string, fontWeight: "600" }}>
                @{parentAuthor}{" "}
              </Text>
            )}
            <LinkifiedSegments
              text={expanded ? comment.content : previewContent}
            />
          </Text>

          {/* Mentioned users — same pill treatment posts use, so a tagged person
              is named rather than being invisible in the comment body. */}
          {(comment.mentionedUsers ?? []).length > 0 && (
            <View className="flex-row flex-wrap w-full pt-1">
              {(comment.mentionedUsers ?? []).map(
                (
                  mentioned: SelectableItem & {
                    firstName?: string;
                    lastName?: string;
                    userId?: string;
                  },
                  index,
                ) => {
                  // Comment mentions arrive from GraphQL as user *snapshots*
                  // ({userId, firstName, lastName, …}) but are typed as
                  // SelectableItem ({itemId, displayName, …}). Both shapes occur
                  // at runtime, so read either rather than trusting one — the
                  // same mismatch is why post-card carries baseline type errors.
                  const name =
                    [mentioned.firstName, mentioned.lastName]
                      .filter(Boolean)
                      .join(" ") ||
                    mentioned.displayName ||
                    "Unknown";
                  const id = mentioned.userId ?? mentioned.itemId;
                  return (
                    <MentionedUserPill
                      key={id ?? `${name}-${index}`}
                      displayName={name}
                      userId={id}
                      isEditing={false}
                    />
                  );
                },
              )}
            </View>
          )}

          {/* See More / See Less */}
          {showSeeMore && (
            // `self-start` so the hover background wraps the label instead of
            // the full width of the comment bubble.
            <Pressable
              onPress={() => setExpanded((prev) => !prev)}
              className="self-start mt-1 -ml-1 px-1.5 py-0.5 rounded-lg hover:bg-ash-3"
            >
              <Text className="text-primary text-xs font-medium">
                {expanded ? t("LABEL_SEE_LESS") : t("LABEL_SEE_MORE")}
              </Text>
            </Pressable>
          )}

          {/* Bottom Actions */}
          <View className="flex-row items-center gap-4 mt-2">
            <LikeButton
              size="sm"
              currentReaction={userReaction?.type ?? null}
              likeAnim={likeAnim}
              onPress={() =>
                handleReactionSelect(userReaction?.type ? null : "like")
              }
              onLongPress={() => {
                if (Platform.OS !== "web") setShowPicker(true);
              }}
              onHoverIn={() => {
                if (Platform.OS === "web") handleHoverIn();
              }}
              onHoverOut={() => {
                if (Platform.OS === "web") handleHoverOut();
              }}
            />

            {showPicker && (
              <ReactionPicker
                onSelect={(reaction) => handleReactionSelect(reaction, true)}
                onClose={() => setShowPicker(false)}
                onStay={() => {
                  if (hoverTimeout.current) {
                    clearTimeout(hoverTimeout.current);
                    hoverTimeout.current = null;
                  }
                }}
                offset={{ bottom: 16, left: 3 }}
              />
            )}

            <View className="h-4 border-r border-ash-3" />

            <TouchableOpacity
              onPress={() =>
                onReply?.(comment.ItemId, comment.authorSnapshot.firstName ?? "")
              }
              className="hover:bg-ash-3 hover:rounded-lg"
            >
              <Text className="text-xs" style={{ color: themeVars["--color-slate-default"] as string }}>
                {t("LABEL_REPLY")} · {comment.replyCount}
              </Text>
            </TouchableOpacity>

            <View className="flex-row items-center">
              {topReactions.map((rxn, index) => (
                <ReactionGlyph
                  key={rxn.id}
                  id={rxn.id}
                  size={14}
                  style={{
                    marginLeft: index === 0 ? 0 : -6,
                    zIndex: topReactions.length - index,
                  }}
                  className="bg-white rounded-full"
                />
              ))}
              <Text
                className="text-xs"
                style={{ color: themeVars["--color-slate-default"] as string, marginLeft: 2 }}
              >
                {reactionTotal === 0
                  ? `0 ${t("LABEL_LIKES")}`
                  : `+${reactionTotal}`}
              </Text>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
};
